// Wiring for a Page/Site (PR 1) or App (PR 2) frame: the creator (UI1's frame mount) calls one
// hook and passes its result to <InspectLayer>. It owns the lifecycle ORCH1 asked for:
//   - INS's createInspectFrameBridge over RUN's transport, with DES2's host geometry
//     (the shared GeoClock's identity, the frame's transform in the layer, the layer viewport);
//   - the frame adapter with INS's manifest projection (keyboard list, Select parent) and
//     host-local transform observation;
//   - refreshes of watched boxes are the layer's (it advances the clock first, then calls
//     adapter.refresh), and disposal of adapter and bridge when the frame or session goes away.
import { useEffect, useMemo, useRef, useState, type RefObject } from 'react';
import {
  createInspectFrameBridge,
  type InspectTransport,
} from '../../../src/apps/selection/frame-bridge.mjs';
import type { InspectSession as DomainSession } from '../../../src/apps/selection/controller.mjs';
import { createFrameAdapter, type FrameManifest } from './adapters/frame';
import { GeoClock, type SurfaceTransform } from './geometry';
import type { InspectAdapter, InspectSession, Rect } from './types';

export type { InspectTransport };

/** The frame's place in the overlay: offset from the overlay's corner, and its CSS scale. */
export function frameTransform(frame: HTMLElement, overlay: HTMLElement): SurfaceTransform {
  const f = frame.getBoundingClientRect();
  const o = overlay.getBoundingClientRect();
  const layoutWidth = frame.offsetWidth || f.width || 1;
  return { offsetX: f.left - o.left, offsetY: f.top - o.top, scale: f.width / layoutWidth || 1 };
}

export function overlayViewport(overlay: HTMLElement): Rect {
  const o = overlay.getBoundingClientRect();
  return { x: 0, y: 0, width: o.width, height: o.height };
}

/**
 * The adapter and the shared geometry clock for an inspected frame, or null until the frame and
 * overlay are mounted. `domain` is INS's InspectSession (the bridge checks its announced
 * requests); `session` is the host-facing projection the layer renders from.
 */
export function useFrameInspectAdapter({
  transport,
  domain,
  session,
  frame,
  overlay,
  manifest,
}: {
  transport: InspectTransport | null;
  domain: DomainSession | null;
  session: Pick<InspectSession, 'sessionId' | 'revision'>;
  frame: RefObject<HTMLElement | null>;
  overlay: RefObject<HTMLElement | null>;
  manifest?: FrameManifest;
}): { adapter: InspectAdapter; clock: GeoClock } | null {
  const clock = useMemo(
    () => new GeoClock(session.sessionId, session.revision),
    [session.sessionId, session.revision],
  );
  const [wired, setWired] = useState<{ adapter: InspectAdapter; clock: GeoClock } | null>(null);
  // The manifest is read through a ref: callers may pass a new object each render, which must
  // not rebuild the bridge (that would loop and drop every watch).
  const manifestRef = useRef(manifest);
  manifestRef.current = manifest;
  const hasManifest = Boolean(manifest);
  useEffect(() => {
    const frameEl = frame.current;
    const overlayEl = overlay.current;
    if (!transport || !domain || !frameEl || !overlayEl) return;
    const bridge = createInspectFrameBridge<SurfaceTransform, Rect>({
      transport,
      session: domain,
      geometry: () => clock.current(),
      transform: () => frameTransform(frameEl, overlayEl),
      viewport: () => overlayViewport(overlayEl),
    });
    const liveManifest: FrameManifest | undefined = hasManifest
      ? {
          list: () => manifestRef.current?.list() ?? [],
          chain: (nodeId) => manifestRef.current?.chain(nodeId) ?? [],
          targetIdFor: (nodeId) => manifestRef.current?.targetIdFor(nodeId) ?? null,
          nodeIdFor: (targetId) => manifestRef.current?.nodeIdFor(targetId) ?? null,
        }
      : undefined;
    const adapter = createFrameAdapter(bridge, liveManifest, frameEl);
    // Refreshing after geometry changes is the layer's (it advances the clock first).
    setWired({ adapter, clock });
    return () => {
      adapter.dispose?.();
      bridge.dispose();
      setWired(null);
    };
  }, [transport, domain, frame, overlay, hasManifest, clock]);
  return wired;
}
